import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TranslationService } from './translation-service';

@Injectable({
  providedIn: 'root'
})
export class TextService {
  private textDataSubject = new BehaviorSubject<any>({});
  public textData$ = this.textDataSubject.asObservable();

  constructor(private http: HttpClient, private i18n: TranslationService) {
    // Reload translation file whenever language changes
    this.i18n.languageChanges$()
      .pipe(
        switchMap(lang => this.http.get(`/assets/data/lan_${lang}.json`))
      )
      .subscribe(data => {
        this.textDataSubject.next(data);
      });
  }

  getTextDataSnapshot(): any {
    return this.textDataSubject.value;
  }

  /** 
   * Enrich the product catalog with translated labels and descriptions.
   * Keep the same structure as product.json: { apps: { products: [...], general: {...} }, ... } 
   */
  injectDescriptionsIntoProducts(catalog: any): any {
    const texts = this.getTextDataSubjectValue();

    const enrich = (p: any) => ({
      ...p,
      name: p.name || texts[p.name_label] || p.name_label,
      description: p.description || texts[p.description_label] || '',
      limit: texts[p.limit_label] || '',
      price_title: texts[p.price_label] || 'price',
      quantity_title: texts[p.quantity_label] || 'quantity',
    });

    return {
      ...catalog,
      apps: {
        ...catalog.apps,
        products: (catalog.apps?.products ?? []).map(enrich),
      },
      office365: {
        ...catalog.office365,
        tabs: (catalog.office365?.tabs ?? []).map((tab: any) => ({
          ...tab,
          products: (tab.products ?? []).map((product: any) => ({
            ...enrich(product),
            addons: (product.addons ?? []).map(enrich),
            annualVariant: product.annualVariant
              ? {
                  ...enrich(product.annualVariant),
                  addons: (product.annualVariant.addons ?? []).map(enrich),
                }
              : undefined,
          })),
        })),
        addons: (catalog.office365?.addons ?? []).map(enrich),
      }
    };
  }


  /** Helper to safely get current text data snapshot */
  private getTextDataSubjectValue() {
    return this.textDataSubject.value || {};
  }

  /** Utility to get a translated string by key */
  getText(key: string): string {
    const texts = this.getTextDataSubjectValue();
    return texts[key] || key;
  }

  /** Example method to load extra attributes */
  getAttributes() {
    return this.http.get('assets/data/domainExtraAttribute.json');
  }
}
